import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

export default function OperatorModule() {
  const [tab, setTab] = useState("chat"); // chat / brief / prefs
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [recentBriefs, setRecentBriefs] = useState([]);
  const scrollRef = useRef(null);

  // ── Veille concurrentielle
  const [competitors, setCompetitors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [scanReport, setScanReport] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [includeGeneral, setIncludeGeneral] = useState(true);
  const [newComp, setNewComp] = useState({ name: "", location: "", discipline: "" });
  const [engine, setEngine] = useState("auto");
  const [pplxOk, setPplxOk] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(null);
  const [fiche, setFiche] = useState(null);
  const enrichir = async (c) => {
    setEnrichBusy(c.id);
    try { const r = await api.post(`/recherche/competitors/${c.id}/enrichir`); setFiche({ name: c.name, ...r.data.enrichissement }); toast.success(`Fiche ${c.name} enrichie (Perplexity)`); await loadCompetitors(); }
    catch (e) { toast.error(e.response?.data?.detail || "Enrichissement impossible", { duration: 9000 }); } finally { setEnrichBusy(null); }
  };

  const loadCompetitors = async () => {
    try { const r = await api.get("/operator/competitors"); setCompetitors(r.data); } catch {}
  };
  const loadRecentScans = async () => {
    try { const r = await api.get("/operator/competitors/scans/recent"); setRecentScans(r.data); } catch {}
  };
  const seedCompetitors = async () => {
    try {
      const r = await api.post("/operator/competitors/seed");
      toast.success(`✅ Liste importée (${r.data.created} ajoutés · ${r.data.total} au total)`);
      await loadCompetitors();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import"); }
  };
  const addCompetitor = async () => {
    if (!newComp.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await api.post("/operator/competitors", newComp);
      setNewComp({ name: "", location: "", discipline: "" });
      toast.success("✅ Concurrent ajouté");
      await loadCompetitors();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur ajout"); }
  };
  const deleteCompetitor = async (cid) => {
    try {
      await api.delete(`/operator/competitors/${cid}`);
      setSelectedIds((s) => s.filter((x) => x !== cid));
      await loadCompetitors();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur suppression"); }
  };
  const toggleSelect = (cid) => {
    setSelectedIds((s) => s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]);
  };
  const runScan = async () => {
    if (competitors.length === 0) { toast.error("Ajoute des concurrents d'abord"); return; }
    setScanBusy(true);
    setScanReport(null);
    try {
      const body = { include_general: includeGeneral, engine };
      if (selectedIds.length > 0) body.competitor_ids = selectedIds;
      const r = await api.post("/operator/competitors/scan", body);
      setScanReport(r.data);
      await loadRecentScans();
      toast.success(`🔭 Veille terminée (${r.data.engine === "perplexity" ? "Perplexity" : "DuckDuckGo"}) · ${r.data.total_signals} signaux web`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur veille");
    } finally { setScanBusy(false); }
  };

  useEffect(() => { if (tab === "veille") { loadCompetitors(); loadRecentScans(); api.get("/recherche/status").then((r) => setPplxOk(!!r.data.configured)).catch(() => setPplxOk(false)); } }, [tab]);

  const loadSessions = async () => {
    try { const r = await api.get("/operator/sessions"); setSessions(r.data); } catch {}
  };
  const loadRecentBriefs = async () => {
    try { const r = await api.get("/operator/briefs/recent"); setRecentBriefs(r.data); } catch {}
  };

  useEffect(() => { loadSessions(); loadRecentBriefs(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadSession = async (sid) => {
    try {
      const r = await api.get(`/operator/sessions/${sid}/messages`);
      setSessionId(sid);
      const msgs = r.data.flatMap(m => [
        { role: "user", text: m.user_message, ts: m.created_at },
        { role: "ai", text: m.ai_response, ts: m.created_at },
      ]);
      setMessages(msgs);
    } catch { toast.error("Erreur chargement"); }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/operator/chat", { message: userMsg, session_id: sessionId, include_context: true });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "ai", text: r.data.response }]);
      await loadSessions();
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "⚠️ Erreur : " + (e.response?.data?.detail || e.message), error: true }]);
    } finally { setBusy(false); }
  };

  const generateBrief = async (timeframe = "today") => {
    setBriefBusy(true);
    setBrief(null);
    try {
      const r = await api.post("/operator/brief", { timeframe });
      setBrief(r.data);
      await loadRecentBriefs();
      toast.success("✅ Brief généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération");
    } finally { setBriefBusy(false); }
  };

  const generateTransitionReport = async () => {
    setBriefBusy(true);
    setBrief(null);
    try {
      const r = await api.post("/operator/transition-report", { days: 7, send_email: false });
      // Présente comme un brief standard (champ .text)
      setBrief({ text: r.data.report, timeframe: "transition_7d", generated_at: new Date().toISOString() });
      toast.success(`📊 Rapport transition · conv ${r.data.conversion_rate}% · revenus sécurisés ${r.data.revenue_secured_monthly} $/mois`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur rapport transition");
    } finally { setBriefBusy(false); }
  };

  const SUGGESTIONS = [
    "Quel est mon sponsor le plus important ?",
    "Combien de billets vendus cette semaine ?",
    "Status livestream pour la Mini Comp ?",
    "Top 3 actions critiques aujourd'hui ?",
    "Mes relances sponsors en retard ?",
  ];

  return (
    <div className="space-y-6" data-testid="operator-module">
      {/* Header */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-emerald-400 text-[10px] tracking-[0.4em] mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          AI OPERATOR · CHIEF OF STAFF
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Kaizen Operator 24/7</h1>
        <p className="text-zinc-500 text-sm mt-2">
          Ton chef de cabinet IA. Connecté aux données réelles (sponsors, mini comps, billetterie, livestream). Pose des questions courtes, reçois des actions claires.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800" data-testid="operator-tabs">
        <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} testid="tab-chat">💬 Chat</TabBtn>
        <TabBtn active={tab === "brief"} onClick={() => setTab("brief")} testid="tab-brief">📋 Brief quotidien</TabBtn>
        <TabBtn active={tab === "veille"} onClick={() => setTab("veille")} testid="tab-veille">🔭 Veille concurrentielle</TabBtn>
      </div>

      {tab === "chat" && (
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Sessions sidebar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Sessions</div>
              <Button size="sm" onClick={newSession} className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs" data-testid="new-session-btn">+ Nouvelle</Button>
            </div>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="sessions-list">
              {sessions.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Aucune conversation. Lance-toi !</div>
              ) : sessions.map(s => (
                <button key={s.session_id} onClick={() => loadSession(s.session_id)} className={`w-full text-left border rounded-lg p-2 text-xs ${sessionId === s.session_id ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-600"}`}>
                  <div className="font-semibold text-zinc-100 truncate">{s.title || "Sans titre"}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{s.count} msg · {new Date(s.last_at).toLocaleDateString("fr-CA")}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Chat panel */}
          <div className="lg:col-span-3 border border-zinc-800 rounded-2xl bg-zinc-950/40 flex flex-col" style={{ minHeight: "70vh" }}>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-zinc-500 text-sm py-12">
                  <div className="text-4xl mb-3">🤖</div>
                  <div>Démarre une conversation. Style WhatsApp : message court = réponse courte.</div>
                  <div className="mt-6 text-[10px] tracking-[0.3em] text-zinc-600 uppercase">SUGGESTIONS</div>
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => setInput(s)} className="text-[11px] border border-zinc-800 bg-black/30 hover:border-emerald-500/40 rounded-full px-3 py-1.5 transition" data-testid={`suggestion-${i}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === "user"
                      ? "bg-amber-500/15 border border-amber-500/30 text-amber-100"
                      : m.error
                        ? "bg-red-500/10 border border-red-500/30 text-red-200"
                        : "bg-zinc-900 border border-zinc-800 text-zinc-100"
                  }`} data-testid={`msg-${m.role}-${i}`}>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl px-4 py-2.5 text-sm text-zinc-400">
                    <span className="inline-block animate-pulse">●</span>
                    <span className="inline-block animate-pulse mx-1" style={{ animationDelay: "150ms" }}>●</span>
                    <span className="inline-block animate-pulse" style={{ animationDelay: "300ms" }}>●</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-zinc-800 p-3 flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !busy) { e.preventDefault(); send(); } }}
                placeholder="Pose ta question…"
                className="bg-black border-zinc-800"
                data-testid="chat-input"
              />
              <Button onClick={send} disabled={busy || !input.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-black" data-testid="send-btn">
                {busy ? "…" : "→"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {tab === "brief" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-[10px] tracking-[0.3em] text-emerald-400">BRIEF KAIZEN</div>
              <div className="flex gap-1">
                <Button onClick={() => generateBrief("morning")} disabled={briefBusy} size="sm" className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-xs" data-testid="brief-morning">🌅 Matin</Button>
                <Button onClick={() => generateBrief("today")} disabled={briefBusy} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs" data-testid="brief-today">📋 Aujourd'hui</Button>
                <Button onClick={() => generateBrief("evening")} disabled={briefBusy} size="sm" className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-xs" data-testid="brief-evening">🌙 Soir</Button>
                <Button onClick={generateTransitionReport} disabled={briefBusy} size="sm" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs" data-testid="brief-transition">📊 Transition ZP→Stripe</Button>
              </div>
            </div>
            {briefBusy ? (
              <div className="text-center text-zinc-500 py-12">
                <div className="text-4xl mb-3">🤖</div>
                <div className="text-sm">Analyse de tes données en cours…</div>
              </div>
            ) : brief ? (
              <div className="space-y-3" data-testid="brief-content">
                <pre className="text-sm text-zinc-100 whitespace-pre-wrap font-sans leading-relaxed">{brief.text}</pre>
                <div className="border-t border-zinc-800 pt-3 mt-4">
                  <Button onClick={() => { navigator.clipboard.writeText(brief.text); toast.success("📋 Brief copié"); }} variant="outline" size="sm" data-testid="copy-brief-btn">
                    📋 Copier
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-500 py-12">
                <div className="text-4xl mb-3">📋</div>
                <div className="text-sm">Génère ton premier brief opérationnel</div>
                <div className="text-xs text-zinc-600 mt-2">Top 3 priorités · Alertes · Opportunité du jour</div>
              </div>
            )}
          </div>

          {/* Recent briefs */}
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Briefs récents</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="recent-briefs">
              {recentBriefs.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Aucun brief encore.</div>
              ) : recentBriefs.map(b => (
                <button key={b.id} onClick={() => setBrief(b)} className={`w-full text-left border rounded-lg p-3 text-xs transition ${brief?.id === b.id ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-600"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline" className="text-[10px]">{b.timeframe}</Badge>
                    <span className="text-[10px] text-zinc-500">{new Date(b.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                  <div className="text-zinc-300 truncate">{(b.text || "").split("\n").find(l => l.trim()) || "Brief"}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "veille" && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="veille-panel">
          {/* Concurrents sidebar */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Concurrents surveillés</div>
              <Button size="sm" onClick={seedCompetitors} className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-[11px]" data-testid="seed-competitors-btn">↻ Liste de départ</Button>
            </div>

            {/* Ajout manuel */}
            <div className="border border-zinc-800 rounded-xl p-3 bg-zinc-950/40 space-y-2">
              <Input value={newComp.name} onChange={(e) => setNewComp({ ...newComp, name: e.target.value })} placeholder="Nom du concurrent *" className="bg-black border-zinc-800 text-xs h-8" data-testid="new-comp-name" />
              <div className="flex gap-2">
                <Input value={newComp.location} onChange={(e) => setNewComp({ ...newComp, location: e.target.value })} placeholder="Ville" className="bg-black border-zinc-800 text-xs h-8" data-testid="new-comp-location" />
                <Input value={newComp.discipline} onChange={(e) => setNewComp({ ...newComp, discipline: e.target.value })} placeholder="Discipline" className="bg-black border-zinc-800 text-xs h-8" data-testid="new-comp-discipline" />
              </div>
              <Button size="sm" onClick={addCompetitor} className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs" data-testid="add-competitor-btn">+ Ajouter</Button>
            </div>

            {/* Liste */}
            <div className="space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="competitors-list">
              {competitors.length === 0 ? (
                <div className="text-xs text-zinc-500 italic px-1">Aucun concurrent. Clique « Liste de départ ».</div>
              ) : competitors.map((c) => (
                <div key={c.id} className={`border rounded-lg p-2.5 text-xs flex items-start gap-2 transition ${selectedIds.includes(c.id) ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/40"}`} data-testid={`competitor-${c.id}`}>
                  <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} className="mt-0.5 accent-emerald-500" data-testid={`competitor-check-${c.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-100 truncate">{c.name}{c.enrichissement && <span className="ml-1 text-[9px] text-emerald-400" title={`Fiche enrichie le ${c.enrichissement.at?.slice(0, 10)}`}>●</span>}</div>
                    <div className="text-[10px] text-zinc-500">{[c.location, c.discipline].filter(Boolean).join(" · ")}</div>
                    {c.enrichissement && <button onClick={() => setFiche({ name: c.name, ...c.enrichissement })} className="text-[10px] text-amber-300 hover:underline" data-testid={`fiche-competitor-${c.id}`}>Voir la fiche</button>}
                  </div>
                  {pplxOk && <button disabled={enrichBusy === c.id} onClick={() => enrichir(c)} className="text-[10px] rounded border border-zinc-700 hover:border-amber-500/60 text-zinc-300 px-1.5 py-0.5 disabled:opacity-40" title="Enrichir la fiche avec Perplexity (site, adresse, réseaux, offres)" data-testid={`enrich-competitor-${c.id}`}>{enrichBusy === c.id ? "…" : "Enrichir"}</button>}
                  <button onClick={() => deleteCompetitor(c.id)} className="text-zinc-600 hover:text-red-400 text-sm leading-none" data-testid={`delete-competitor-${c.id}`} title="Supprimer">×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Fiche enrichie (Perplexity) */}
          {fiche && <div className="lg:col-span-3 lg:order-last border border-amber-500/30 rounded-2xl p-5 bg-amber-500/5 text-xs space-y-2" data-testid="fiche-enrichie">
            <div className="flex items-center justify-between gap-2"><b className="text-sm text-zinc-100">{fiche.name} — fiche enrichie (Perplexity)</b><span className="text-zinc-500">confiance {fiche.confidence} · {fiche.at?.slice(0, 10)}</span><button onClick={() => setFiche(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="fiche-close">×</button></div>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-zinc-300">
              <div>Site : {fiche.website ? <a href={fiche.website} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline">{fiche.website}</a> : "—"}</div>
              <div>Adresse : {fiche.address || "—"}</div><div>Téléphone : {fiche.phone || "—"}</div>
              <div>Réseaux : {fiche.socials?.length ? fiche.socials.map((s) => <a key={s} href={s} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline mr-2">{s.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}</a>) : "—"}</div>
              <div>Disciplines : {fiche.disciplines?.join(", ") || "—"}</div><div>Horaire : {fiche.schedule_summary || "—"}</div>
            </div>
            {fiche.offers?.length > 0 && <div><b className="text-zinc-200">Offres / tarifs</b><ul className="list-disc pl-4 text-zinc-300">{fiche.offers.map((o, i) => <li key={i}>{o}</li>)}</ul></div>}
            {fiche.recent_news?.length > 0 && <div><b className="text-zinc-200">Nouveautés récentes</b><ul className="list-disc pl-4 text-zinc-300">{fiche.recent_news.map((o, i) => <li key={i}>{o}</li>)}</ul></div>}
            {fiche.citations?.length > 0 && <div className="text-zinc-500">Sources : {fiche.citations.map((c, i) => <a key={c} href={c} target="_blank" rel="noreferrer" className="hover:text-amber-300 mr-2">[{i + 1}]</a>)}</div>}
          </div>}

          {/* Rapport de veille */}
          <div className="lg:col-span-2 border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-[10px] tracking-[0.3em] text-emerald-400">VEILLE WEB · {pplxOk && engine !== "duckduckgo" ? "PERPLEXITY" : "DUCKDUCKGO"} + IA</div>
              <div className="flex items-center gap-3">
                {pplxOk && <select value={engine} onChange={(e) => setEngine(e.target.value)} className="rounded bg-black border border-zinc-800 px-2 py-1 text-[11px] text-zinc-300" data-testid="veille-engine"><option value="auto">Perplexity (sourcé)</option><option value="duckduckgo">DuckDuckGo (gratuit)</option></select>}
                <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer" data-testid="include-general-label">
                  <input type="checkbox" checked={includeGeneral} onChange={(e) => setIncludeGeneral(e.target.checked)} className="accent-emerald-500" data-testid="include-general-check" />
                  Tendances régionales
                </label>
                <Button onClick={runScan} disabled={scanBusy} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs" data-testid="run-scan-btn">
                  {scanBusy ? "Analyse…" : (selectedIds.length > 0 ? `🔭 Veiller (${selectedIds.length})` : "🔭 Lancer la veille")}
                </Button>
              </div>
            </div>

            {scanBusy ? (
              <div className="text-center text-zinc-500 py-12">
                <div className="text-4xl mb-3">🔭</div>
                <div className="text-sm">Recherche web sur tes concurrents en cours…</div>
                <div className="text-xs text-zinc-600 mt-2">DuckDuckGo + synthèse Claude · ~30s</div>
              </div>
            ) : scanReport ? (
              <div className="space-y-3" data-testid="scan-report">
                <div className="text-[11px] text-zinc-500">{scanReport.competitors_count} concurrent(s) · {scanReport.total_signals} signaux web collectés</div>
                <pre className="text-sm text-zinc-100 whitespace-pre-wrap font-sans leading-relaxed">{scanReport.report}</pre>
                <div className="border-t border-zinc-800 pt-3 mt-4">
                  <Button onClick={() => { navigator.clipboard.writeText(scanReport.report); toast.success("📋 Veille copiée"); }} variant="outline" size="sm" data-testid="copy-scan-btn">📋 Copier</Button>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-500 py-12">
                <div className="text-4xl mb-3">🔭</div>
                <div className="text-sm">Lance une veille concurrentielle</div>
                <div className="text-xs text-zinc-600 mt-2">Sélectionne des concurrents (ou laisse vide = tous) puis « Lancer la veille »</div>
              </div>
            )}

            {/* Historique */}
            {recentScans.length > 0 && (
              <div className="mt-6 border-t border-zinc-800 pt-4">
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Veilles récentes</div>
                <div className="space-y-2 max-h-[30vh] overflow-y-auto" data-testid="recent-scans">
                  {recentScans.map((s) => (
                    <button key={s.id} onClick={async () => { try { const r = await api.get(`/operator/competitors/scans/${s.id}`); setScanReport({ ...r.data, report: r.data.report_text }); } catch {} }} className="w-full text-left border border-zinc-800 bg-zinc-950/40 hover:border-zinc-600 rounded-lg p-2.5 text-xs transition" data-testid={`scan-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-[10px]">{s.competitors_count} concurrents · {s.total_signals} signaux</Badge>
                        <span className="text-[10px] text-zinc-500">{new Date(s.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${active ? "border-emerald-500 text-emerald-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
